import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { AppUpdateBanner } from '@cavela/ui';

import useServiceWorkerUpdate from './useServiceWorkerUpdate';

/**
 * Persistent top banner that appears when a newly deployed service worker
 * is installed and waiting to take over. See `useServiceWorkerUpdate.ts`
 * for the update flow details.
 *
 * Mounted inside <ErrorBoundaryLayout /> so it lives inside the React
 * Router tree (needed for useLocation) and renders on every route.
 *
 * Intentionally not dismissible — users should pick up the new build.
 *
 * Fresh-open behaviour: if the update was *detected* during the initial
 * page-load window (i.e. `isMidSessionUpdate` is false), the update is
 * applied silently. The banner only appears when the update was detected
 * after the session was established — meaning a deploy landed while the
 * user was already actively using the tab.
 *
 * This is robust to slow production installs: classification is anchored
 * to when the browser *finds* the new SW (a quick script fetch regardless
 * of bundle size), not to when precaching finishes.
 */
const VersionUpdateBanner = () => {
  const { needsRefresh, isMidSessionUpdate, checkForUpdate, applyUpdate } =
    useServiceWorkerUpdate();
  const location = useLocation();
  const [isReloading, setIsReloading] = useState(false);

  const handleReload = () => {
    setIsReloading(true);
    applyUpdate();
  };

  // On a fresh page open the update is detected before the session window
  // elapses, so isMidSessionUpdate is false — apply silently without showing
  // the banner. After the reload, the new SW is the controller and there is
  // no waiting SW, so needsRefresh resets to false.
  useEffect(() => {
    if (needsRefresh && !isMidSessionUpdate) {
      applyUpdate();
    }
  }, [needsRefresh, isMidSessionUpdate, applyUpdate]);

  // Trigger an update check whenever the user shows signs of activity.
  // This catches deploys quickly without running a continuous timer
  // on idle tabs.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    };
    const onFocus = () => {
      void checkForUpdate();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    void checkForUpdate();

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [checkForUpdate]);

  useEffect(() => {
    void checkForUpdate();
  }, [location.pathname, checkForUpdate]);

  if (!needsRefresh || !isMidSessionUpdate) return null;

  return (
    <AppUpdateBanner
      appName="Cavela"
      onReload={handleReload}
      isLoading={isReloading}
    />
  );
};

export default VersionUpdateBanner;
